
import { supabase } from '@/integrations/supabase/client';

export type ActivityType = 
  | 'login' 
  | 'data_export'
  | 'mfa_verification'
  | 'mfa_enrollment'
  | 'merchant_type_change'
  // PO activity types
  | 'po_created'
  | 'po_status_changed'
  | 'po_archived'
  | 'po_line_item_updated'
  | 'po_line_item_verified'

interface ActivityLogData {
  userId?: string;
  userEmail: string;
  activityType: ActivityType;
  activityDetails?: any;
  ipAddress?: string;
  userAgent?: string;
}

const createTimeoutPromise = (timeoutMs: number) => {
  return new Promise((_, reject) => {
    setTimeout(() => reject(new Error('Timeout')), timeoutMs);
  });
};

export const logUserActivity = async ({
  userId,
  userEmail,
  activityType,
  activityDetails,
  ipAddress,
  userAgent
}: ActivityLogData): Promise<boolean> => {
  try {

    const timeoutPromise = createTimeoutPromise(2000);
    const rpc = (supabase.rpc as any)('log_user_activity', {
      p_user_id: userId || null,
      p_user_email: userEmail,
      p_activity_type: activityType,
      p_activity_details: activityDetails || null,
      p_ip_address: ipAddress || null,
      p_user_agent: userAgent || navigator.userAgent
    });

    type ResultType = { error?: any };

    const result: ResultType = await Promise.race([rpc, timeoutPromise]);
    const { error } = result;

    if (error) {
      console.error('[ActivityLogger] Error logging user activity:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('[ActivityLogger] Error logging user activity:', error);
    return false;
  }
};

// Helper function to get user's IP address (simplified version)
export const getUserIP = async (): Promise<string | null> => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error('[ActivityLogger] Error getting user IP:', error);
    return null;
  }
};

// Helper function to log login activities
export const logLoginActivity = async (
  userEmail: string, 
  method: 'password' | 'magic_link', 
  userId?: string,
  submissionIdentifier?: string
) => {
  try {
    const ipAddress = await getUserIP();
    
    const activityDetails = {
      method,
      timestamp: new Date().toISOString(),
      ...(submissionIdentifier && { submission_identifier: submissionIdentifier })
    };

    return await logUserActivity({
      userId,
      userEmail,
      activityType: 'login',
      activityDetails,
      ipAddress
    });
  } catch (error) {
    console.error('[ActivityLogger] Error logging login activity:', error);
    return false;
  }
};

// Helper function to log MFA verification events
export const logMfaVerification = async (userEmail: string, success: boolean, factorType: 'email' | 'sms' = 'email') => {
  const ipAddress = await getUserIP();
  
  return logUserActivity({
    userEmail,
    activityType: 'mfa_verification',
    activityDetails: {
      success,
      factorType,
      timestamp: new Date().toISOString()
    },
    ipAddress
  });
};

// Helper function to log MFA enrollment events
export const logMfaEnrollment = async (userEmail: string, factorType: 'email' | 'sms' = 'email') => {
  const ipAddress = await getUserIP();
  
  return logUserActivity({
    userEmail,
    activityType: 'mfa_enrollment',
    activityDetails: {
      factorType,
      timestamp: new Date().toISOString()
    },
    ipAddress
  });
};

// Helper function to log merchant type changes
export const logMerchantTypeChange = async (
  userEmail: string,
  organisationId: string,
  organisationName: string,
  oldType: string,
  newType: string
) => {
  const ipAddress = await getUserIP();
  
  return logUserActivity({
    userEmail,
    activityType: 'merchant_type_change',
    activityDetails: {
      organisation_id: organisationId,
      organisation_name: organisationName,
      old_value: oldType,
      new_value: newType,
      timestamp: new Date().toISOString()
    },
    ipAddress
  });
};

// Helper function to log PO status changes
export const logPOStatusChange = async (
  userEmail: string,
  purchaseOrderId: string,
  poNumber: string,
  oldStatus: string,
  newStatus: string
) => {
  const ipAddress = await getUserIP();
  
  return logUserActivity({
    userEmail,
    activityType: 'po_status_changed',
    activityDetails: {
      purchase_order_id: purchaseOrderId,
      po_number: poNumber,
      old_value: oldStatus,
      new_value: newStatus,
      timestamp: new Date().toISOString()
    },
    ipAddress
  });
};

// Helper function to log PO archive actions
export const logPOArchive = async (
  userEmail: string,
  purchaseOrderId: string,
  poNumber: string,
  reason: string
) => {
  const ipAddress = await getUserIP();
  
  return logUserActivity({
    userEmail,
    activityType: 'po_archived',
    activityDetails: {
      purchase_order_id: purchaseOrderId,
      po_number: poNumber,
      reason,
      timestamp: new Date().toISOString()
    },
    ipAddress
  });
};

// Helper function to log PO line item updates
export const logPOLineItemUpdate = async (
  userEmail: string,
  purchaseOrderId: string,
  lineItemId: string,
  changes: Record<string, { old: unknown; new: unknown }>
) => {
  const ipAddress = await getUserIP();
  
  return logUserActivity({
    userEmail,
    activityType: 'po_line_item_updated',
    activityDetails: {
      purchase_order_id: purchaseOrderId,
      line_item_id: lineItemId,
      changes,
      timestamp: new Date().toISOString()
    },
    ipAddress
  });
};

// Helper function to log PO line item verification
export const logPOLineItemVerification = async (
  userEmail: string,
  purchaseOrderId: string,
  lineItemIds: string[],
  isBulk: boolean = false
) => {
  const ipAddress = await getUserIP();
  
  return logUserActivity({
    userEmail,
    activityType: 'po_line_item_verified',
    activityDetails: {
      purchase_order_id: purchaseOrderId,
      line_item_ids: lineItemIds,
      is_bulk: isBulk,
      timestamp: new Date().toISOString()
    },
    ipAddress
  });
};
